import { supabase } from "@/services/supabase/client";

export type SortOrder = "asc" | "desc";
export type IncompleteUserSortBy = "created_at" | "plan" | "missing";

export interface IncompleteUser {
  id: string;
  display_name: string | null;
  email: string | null;
  phone: string | null;
  created_at: string;
  has_avatar: boolean;
  has_bio: boolean;
  has_roles: boolean;
  has_locations: boolean;
  phone_verified: boolean;
  has_active_plan: boolean;
  plan?: string | null;
}

export interface IncompleteUsersResponse {
  items: IncompleteUser[];
  total: number;
  has_more: boolean;
}

export interface GetIncompleteUsersParams {
  limit: number;
  offset: number;
  search?: string | null | undefined;
  sortBy?: IncompleteUserSortBy;
  sortOrder?: SortOrder;
}

interface RpcEnvelope {
  ok: boolean;
  error?: string;
  items?: IncompleteUser[];
  total?: number;
  has_more?: boolean;
}

const getIncompleteUsers = async ({
  limit,
  offset,
  search,
  sortBy,
  sortOrder = "desc",
}: GetIncompleteUsersParams): Promise<IncompleteUsersResponse> => {
  const { data, error } = await supabase.rpc("get_admin_incomplete_users", {
    _limit: limit,
    _offset: offset,
    _sort_order: sortOrder,
    ...(sortBy ? { _sort_by: sortBy } : {}),
    ...(search ? { _query_text: search } : {}),
  });

  if (error) {
    console.error("getIncompleteUsers error:", error);
    throw new Error(error.message || "Failed to fetch incomplete users");
  }

  const result = data as RpcEnvelope;
  if (!result?.ok) {
    throw new Error(result?.error || "Failed to fetch incomplete users");
  }

  return {
    items: result.items ?? [],
    total: result.total ?? 0,
    has_more: result.has_more ?? false,
  };
};

const IncompleteUsersService = { getIncompleteUsers };

export default IncompleteUsersService;
